import { useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import logo from '../Assets/Images/logo.svg';
import Button from '../components/ui/Button';
import Icon from '../components/ui/Icon';
import FieldError from '../components/ui/FieldError';
import { login } from '../api/auth';
import { ApiError } from '../api/client';
import { isAuthenticated, saveSession } from '../auth/session';
import { validateEmail } from '../utils/validation';

const MIN_PASSWORD_LENGTH = 6;

function validate({ email, password }) {
  const errors = {};
  const emailError = validateEmail(email);
  if (emailError) errors.email = emailError;

  if (!password) errors.password = 'Please enter your password.';
  else if (password.length < MIN_PASSWORD_LENGTH)
    errors.password = `Password must be at least ${MIN_PASSWORD_LENGTH} characters.`;
  return errors;
}

/** Only same-app paths ("/users?page=2"), never "//host" or absolute URLs (open redirect). */
function safeReturnTo(value) {
  if (!value || !value.startsWith('/') || value.startsWith('//') || value.startsWith('/\\')) return null;
  if (value === '/login' || value.startsWith('/login?')) return null;
  return value;
}

const inputBase =
  'w-full h-10 px-3 text-sm bg-surface-container-lowest border rounded-lg text-on-surface placeholder:text-on-surface-variant/60 outline-none transition';
const inputOk =
  'border-outline-variant/50 focus:border-primary-container focus:shadow-[0_0_0_3px_rgba(34,197,94,0.15)]';
const inputInvalid =
  'border-error bg-status-alert-bg/40 focus:border-error focus:shadow-[0_0_0_3px_rgba(186,26,26,0.15)]';

export default function LoginPage() {
  const location = useLocation();
  // Prefilled after a password reset (router state, never the URL)
  const [values, setValues] = useState(() => ({ email: location.state?.email ?? '', password: '' }));
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [serverError, setServerError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const from = location.state?.from;
  const redirectTo =
    safeReturnTo(new URLSearchParams(location.search).get('returnTo')) ??
    (from ? `${from.pathname}${from.search ?? ''}` : '/scan-details');

  if (isAuthenticated()) return <Navigate to={redirectTo} replace />;

  const handleChange = (e) => {
    const next = { ...values, [e.target.name]: e.target.value };
    setValues(next);
    setServerError('');
    // After the first submit attempt, re-check as the user types so errors clear live
    if (submitted) setFieldErrors(validate(next));
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setSubmitted(true);
    setServerError('');

    const errors = validate(values);
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      document.getElementById(errors.email ? 'login-email' : 'login-password')?.focus();
      return;
    }

    setSubmitting(true);
    try {
      const res = await login(values.email.trim(), values.password);
      if (!res?.data?.token) throw new ApiError('Login failed: the server did not return a session token.');
      saveSession(res.data);
      toast.success(res.message || 'Logged in');
      navigate(redirectTo, { replace: true });
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Something went wrong. Please try again.';
      setServerError(message);
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <div className="w-full max-w-sm bg-surface-container-lowest rounded-2xl border border-outline-variant/30 shadow-[0_10px_15px_-3px_rgba(15,23,42,0.08),0_4px_6px_-4px_rgba(15,23,42,0.04)] p-8">
        <div className="flex flex-col items-center text-center mb-6">
          <img src={logo} alt="Pompi" className="w-55 h-55 rounded-full object-cover -mb-10" />
          <h1 className="text-xl font-bold text-primary tracking-tight">Pompi Admin</h1>
          <p className="text-xs text-on-surface-variant mt-1">Sign in to the operations portal</p>
        </div>

        <form onSubmit={handleLogin} noValidate className="space-y-3">
          {serverError && (
            <div
              role="alert"
              className="flex items-start gap-2 rounded-lg border border-status-alert-border bg-status-alert-bg px-3 py-2.5 text-xs text-status-alert-text"
            >
              <Icon name="error" size={16} />
              <span>{serverError}</span>
            </div>
          )}

          <div>
            <label htmlFor="login-email" className="block text-xs font-semibold text-on-surface-variant mb-1.5">
              E-mail
            </label>
            <input
              id="login-email"
              name="email"
              type="email"
              autoComplete="username"
              placeholder="name@example.com"
              value={values.email}
              onChange={handleChange}
              aria-invalid={Boolean(fieldErrors.email)}
              aria-describedby={fieldErrors.email ? 'login-email-error' : undefined}
              className={`${inputBase} ${fieldErrors.email ? inputInvalid : inputOk}`}
            />
            <FieldError id="login-email-error">{fieldErrors.email}</FieldError>
          </div>

          <div>
            <label htmlFor="login-password" className="block text-xs font-semibold text-on-surface-variant mb-1.5">
              Password
            </label>
            <div className="relative">
              <input
                id="login-password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                placeholder="Enter your password"
                value={values.password}
                onChange={handleChange}
                aria-invalid={Boolean(fieldErrors.password)}
                aria-describedby={fieldErrors.password ? 'login-password-error' : undefined}
                className={`${inputBase} pr-10 ${fieldErrors.password ? inputInvalid : inputOk}`}
              />
              <button
                type="button"
                onClick={() => setShowPassword((shown) => !shown)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                aria-pressed={showPassword}
                aria-controls="login-password"
                title={showPassword ? 'Hide password' : 'Show password'}
                className="absolute right-1.5 top-1/2 -translate-y-1/2 p-1.5 rounded-md text-on-surface-variant hover:text-on-surface hover:bg-surface-container transition-colors outline-none focus-visible:ring-2 focus-visible:ring-status-ok-dot"
              >
                <Icon name={showPassword ? 'visibility_off' : 'visibility'} size={20} />
              </button>
            </div>
            <FieldError id="login-password-error">{fieldErrors.password}</FieldError>
          </div>

          <div className="flex justify-end">
            <Link
              to="/forgot-password"
              state={{ email: values.email.trim() }}
              className="text-xs font-medium text-secondary hover:underline"
            >
              Forgot password?
            </Link>
          </div>

          <Button type="submit" size="lg" className="w-full" disabled={submitting}>
            {submitting ? 'Signing in…' : 'Sign in'}
          </Button>
        </form>
      </div>
    </div>
  );
}
